import Axios from 'axios';
import {
  ChatListRequest,
  ChatMessageListRequest,
  ChatMessageSendRequest,
  ChatMessageReadRequest,
  ChatMessageDeleteRequest
} from '@/models/chatRequest';
import { Chat, ChatListResponse, ChatMessageListResponse, RecentlyChatListResponse } from '@/models/chatResponse';
import { AttachmentFileRequest, AttachmentFile } from '@/models/attachmentFile';
import { ReadTypes } from '@/enums/readTypes';

export interface SearchCondition {
  readType: ReadTypes;
  dateFrom: Date | null;
  dateTo: Date | null;
  ownOnly: boolean;
  keyWord: string;
  perPage: number;
  page: number;
  sort: string;
}

interface State {
  loading: boolean;
  countAll: number;
  chatList: Chat[];
  searchCondition: SearchCondition;
  selectedChat: ChatMessageListResponse | null;
}
const mapState: State = {
  loading: false,
  countAll: 0,
  chatList: [],
  searchCondition: {
    readType: ReadTypes.All,
    dateFrom: null,
    dateTo: null,
    ownOnly: true,
    keyWord: '',
    perPage: 20,
    page: 1,
    sort: 'updateDate descending'
  },
  selectedChat: null,
};

const mapActions = {
  async searchChatList(
    { dispatch, commit }: any,
    searchCondition: SearchCondition
  ) {
    commit('setLoading', true);
    const data = await dispatch('getChatList', searchCondition).catch(
      (error: any) => {
        commit('setLoading', false);
        throw error;
      }
    );
    commit('searchChatList', data);
  },
  async getChatList({ commit }: any, searchCondition: SearchCondition) {
    const request: ChatListRequest = {
      readType: searchCondition.readType,
      dateFrom: searchCondition.dateFrom,
      dateTo: searchCondition.dateTo,
      ownOnly: searchCondition.ownOnly,
      keyWord: searchCondition.keyWord,
      perPage: searchCondition.perPage,
      page: searchCondition.page,
      sort: searchCondition.sort
    };
    const response = await Axios.post<ChatListResponse>('/api/Chat/SearchList', request).catch(
      (error) => {
        throw error;
      }
    );
    response.data.chatList.forEach((chat) => {
      if (typeof chat.updateDate === 'string') {
        chat.updateDate = new Date(chat.updateDate + 'Z');
      }
    });
    return response.data;
  },
  async getRecentlyMessages({ commit }: any, searchCondition: SearchCondition) {
    const request: ChatListRequest = {
      readType: searchCondition.readType,
      dateFrom: searchCondition.dateFrom,
      dateTo: searchCondition.dateTo,
      ownOnly: searchCondition.ownOnly,
      keyWord: searchCondition.keyWord,
      perPage: searchCondition.perPage,
      page: searchCondition.page,
      sort: searchCondition.sort
    };
    const response = await Axios.post<RecentlyChatListResponse>('/api/Chat/RecentlyList', request).catch(
      (error) => {
        throw error;
      }
    );
    response.data.recentlyChatMessageList.forEach((chat) => {
      if (typeof chat.updateDate === 'string') {
        chat.updateDate = new Date(chat.updateDate + 'Z');
      }
    });
    return response.data;
  },
  async selectChat({ dispatch, commit }: any, request: ChatMessageListRequest) {
    commit('setLoading', true);
    const data = await dispatch('getChatMessageList', request).catch(
      (error: any) => {
        commit('setLoading', false);
        throw error;
      }
    );
    commit('selectChat', data);
  },
  async getChatMessageList({ commit }: any, request: ChatMessageListRequest) {
    const response = await Axios.post<ChatMessageListResponse>('/api/Chat/Messages', request).catch(
      (error) => {
        throw error;
      }
    );
    response.data.chatMessageList.forEach((item) => {
      if (typeof item.sendDate === 'string') {
        item.sendDate = new Date(item.sendDate + 'Z');
      }
    });
    return response.data;
  },
  async sendChatMessage(
    { commit }: any,
    {
      tutorId,
      studentId,
      schoolId,
      message,
      imageList
    }: {
      tutorId: string;
      studentId: string;
      schoolId: string;
      message: string;
      imageList: AttachmentFile[];
    }
  ) {
    const urlList: AttachmentFileRequest[] = [];
    if (imageList.length === 0) {
      if (!message || message === '') {
        throw new Error('メッセージを入力してください。');
      }
    } else {
      // ファイルアップロード
      for (const file of imageList) {
        const formData = new FormData();
        formData.append('file', file.raw as File);
        try {
          const uploadResponse = await Axios.post(
            `/api/File/Upload?keyPath=${schoolId}/ChatMessage`,
            formData,
            {
              headers: { 'Content-Type': 'multipart/form-data' }
            }
          );
          urlList.push({
            url: uploadResponse.data as string,
            name: file.name
          });
        } catch (error) {
          // TODO:何かしらのエラー処理？
          return {
            url: '',
            name: file.name
          };
        }
      }
    }

    const request: ChatMessageSendRequest = {
      tutorId,
      studentId,
      attachments: urlList,
      message: imageList.length === 0 ? message : ''
    };
    const response = await Axios.post(
      '/api/Chat/SendMessage',
      request
    ).catch((error) => {
      throw new Error('メッセージの登録に失敗しました。');
    });
    return response.data;
  },
  async readMessage({ commit }: any, messageIdList: string[]) {
    const dataList = await Promise.all(
      messageIdList.map(async (messageId) => {
        const request: ChatMessageReadRequest = {
          id: messageId,
        };
        const response = await Axios.post(
          '/api/Chat/ReadMessage',
          request
        ).catch((error) => {
          throw new Error('既読登録に失敗しました。');
        });
        return response.data;
      })
    );
    return dataList;
  },
  async deleteChatMessage({ commit }: any, messageId: string) {
    const request: ChatMessageDeleteRequest = {
      id: messageId,
    };
    const response = await Axios.post('/api/Chat/DeleteMessage', request).catch(
      (error) => {
        const message = error.response.data;
        if (message) {
          throw new Error(message);
        } else {
          throw new Error('メッセージの削除に失敗しました。');
        }
      }
    );
    commit('removeMessage', messageId);
    return response.data;
  },
  async getAttachment({ commit }: any, url: string) {
    const response = await Axios.get(url, { responseType: 'blob' }).catch(
      (error) => {
        throw error;
      }
    );

    return response.data;
  }
};

const mapMutations = {
  setLoading(state: State, loading: boolean) {
    state.loading = loading;
  },
  searchChatList(state: State, data: ChatListResponse) {
    state.countAll = data.countAll;
    state.chatList = data.chatList;
    state.loading = false;
  },
  selectChat(state: State, data: ChatMessageListResponse) {
    state.selectedChat = data;
    state.loading = false;
  },
};

export default {
  namespaced: true,
  state: mapState,
  actions: mapActions,
  mutations: mapMutations
};
